import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Avatar,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Button,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import apiClient from '../lib/api/apiClient';
import TeamLogo from './TeamLogo';
import { getTeamName, getTeamLogo } from '../utils/getTeamInfo';
import { ROSTER_SECTIONS_CONFIG } from '../constants';
import { httpsPlayerImageUrl } from '../utils/stringUtils';
import { formatPositionsList } from '../utils/formatPositionsList';

interface Player {
  id: number;
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2?: string;
    position_3?: string;
    seasonPositions?: {
      primaryPosition?: string[] | null;
      secondaryPosition?: string[] | null;
      tertiaryPosition?: string[] | null;
    } | null;
    mlb_team?: string;
    image?: string;
    bats?: string;
    throws?: string;
  };
  playerContract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  contract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  status?: string;
  level?: string;
  is_40_man?: boolean;
  team_id?: number;
  year?: number;
  trade_status?: string | null;
  trade_status_until?: string | null;
  deal_status?: string | null;
  // Some APIs attach richer deal data; keep as unknown to avoid strict typing here
  dealData?: unknown;
}

/** POS line from season buckets (`player.*` or `rwPlayer.seasonPositions`), same as roster tables. */
function formatPlayerPositionsLine(player: Player): string {
  const sp = player.rwPlayer?.seasonPositions;
  return formatPositionsList({
    primaryPosition: (player.primaryPosition ?? sp?.primaryPosition) ?? undefined,
    secondaryPosition: (player.secondaryPosition ?? sp?.secondaryPosition) ?? undefined,
    tertiaryPosition: (player.tertiaryPosition ?? sp?.tertiaryPosition) ?? undefined,
  });
}

interface Team {
  id: number;
  name: string;
  abbreviation?: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  prefix?: string;
}

interface PlayerSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamInfo?: Team;
  onPlayerSelect: (players: Player[]) => void;
  selectedPlayers: Player[];
}

const PlayerSelectionModal: React.FC<PlayerSelectionModalProps> = ({
  isOpen,
  onClose,
  teamId,
  teamInfo,
  onPlayerSelect,
  selectedPlayers,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalSelectedPlayers, setModalSelectedPlayers] = useState<Player[]>([]);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const loadAvailablePlayers = async () => {
    if (!teamId) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/roster/full-roster/${teamId}`);
      const players = response.data?.players || [];
      
      // Filter out players whose team_id doesn't match the selected team
      // (exclude players with carried salaries from other teams)
      const filteredPlayers = players.filter((player: Player) => {
        return player.team_id === teamId;
      });
      
      // Sort players by last name, then first name (section grouping will be done separately)
      const sortedPlayers = filteredPlayers.sort((a: Player, b: Player) => {
        // Then by last name
        const lastNameA = a.rwPlayer.last_name || '';
        const lastNameB = b.rwPlayer.last_name || '';
        if (lastNameA !== lastNameB) {
          return lastNameA.localeCompare(lastNameB);
        }
        
        // Finally by first name
        const firstNameA = a.rwPlayer.first_name || '';
        const firstNameB = b.rwPlayer.first_name || '';
        return firstNameA.localeCompare(firstNameB);
      });
      
      setAvailablePlayers(sortedPlayers);
    } catch (error) {
      console.error('Error loading available players:', error);
      setAvailablePlayers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && teamId) {
      loadAvailablePlayers();
      setSearchTerm('');
      // Initialize with the passed selected players (for editing existing selections)
      setModalSelectedPlayers(selectedPlayers);
    }
  }, [isOpen, teamId, selectedPlayers]);

  const handlePlayerSelect = (player: Player) => {
    // Check if player is already selected
    const isAlreadySelected = modalSelectedPlayers.some(p => p.id === player.id);
    
    if (isAlreadySelected) {
      // Remove player if already selected
      setModalSelectedPlayers(prev => prev.filter(p => p.id !== player.id));
    } else {
      // Add player if not selected
      setModalSelectedPlayers(prev => [...prev, player]);
    }
  };

  const handleRemovePlayer = (playerId: number) => {
    setModalSelectedPlayers(prev => prev.filter(p => p.id !== playerId));
  };

  const handleDone = () => {
    onPlayerSelect(modalSelectedPlayers);
    onClose();
  };

  const getFilteredPlayers = () => {
    if (!searchTerm) return availablePlayers;
    
    return availablePlayers.filter(player => {
      const fullName = `${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`.toLowerCase();
      const posLine = formatPlayerPositionsLine(player);
      const posForSearch =
        posLine === '—'
          ? ''
          : posLine.replace(/\//g, ' ').replace(/,/g, ' ').toUpperCase();

      const searchLower = searchTerm.toLowerCase();
      const searchUpper = searchTerm.toUpperCase();
      return fullName.includes(searchLower) || posForSearch.includes(searchUpper);
    });
  };

  // Function to determine which section a player belongs to (matching RosterPage logic)
  const getPlayerSection = (player: Player): string => {
    // Find the order number for AAA
    const AAA_ORDER = ROSTER_SECTIONS_CONFIG.find(sec => sec.label === 'AAA')?.order ?? 11;
    
    // Check if player has "Awaiting Assignment" status or free agent deal_status
    if (player.status === 'Awaiting Assignment' || 
        (player.deal_status && player.deal_status.toLowerCase().includes('awaiting_assignment'))) {
      return 'Unassigned';
    }
    
    if (player.deal_status && player.deal_status.toLowerCase().includes('free')) {
      return 'Unassigned';
    }
    
    // Match player to section using same logic as RosterPage
    for (const section of ROSTER_SECTIONS_CONFIG) {
      let match = false;
      if (section.order < AAA_ORDER) {
        // MLB-level sections: require MLB level and status match
        match =
          player.level?.toLowerCase() === 'mlb' &&
          section.status.some(
            s => s === player.status?.toLowerCase()
          );
      } else {
        // Minor league sections: match by level only
        const playerLevel = player.level?.toLowerCase();
        match = section.level.toLowerCase() === playerLevel;
        if (!match && (section as any).levelAliases) {
          match = (section as any).levelAliases.some((alias: string) => 
            alias.toLowerCase() === playerLevel
          );
        }
      }
      if (match) {
        return section.label;
      }
    }
    
    // If no match, return "Unassigned"
    return 'Unassigned';
  };

  const getPlayersBySection = (): Array<[string, Player[]]> => {
    const filteredPlayers = getFilteredPlayers();
    const grouped: { [sectionLabel: string]: Player[] } = {};
    
    // Initialize all sections from config
    ROSTER_SECTIONS_CONFIG.forEach(section => {
      grouped[section.label] = [];
    });
    grouped['Unassigned'] = [];
    
    // Assign players to sections
    filteredPlayers.forEach(player => {
      const sectionLabel = getPlayerSection(player);
      if (!grouped[sectionLabel]) {
        grouped[sectionLabel] = [];
      }
      grouped[sectionLabel].push(player);
    });
    
    // Return sections as array of tuples, sorted by order from ROSTER_SECTIONS_CONFIG
    const sortedSections: Array<[string, Player[]]> = [];
    
    // Add sections from config in order (only if they have players)
    ROSTER_SECTIONS_CONFIG.forEach(section => {
      if (grouped[section.label] && grouped[section.label].length > 0) {
        sortedSections.push([section.label, grouped[section.label]]);
      }
    });
    
    // Then add unassigned if any (at the end)
    if (grouped['Unassigned'] && grouped['Unassigned'].length > 0) {
      sortedSections.push(['Unassigned', grouped['Unassigned']]);
    }
    
    return sortedSections;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor}>
        <ModalHeader>
          <HStack>
            {teamInfo && (
              <TeamLogo 
                team={{
                  name: getTeamName(teamInfo || null),
                  abbreviation: teamInfo?.abbreviation,
                  logo: getTeamLogo(teamInfo || null),
                  color_primary: teamInfo.color_primary,
                  color_secondary: teamInfo.color_secondary,
                  color_text: teamInfo.color_text
                }} 
                size="sm" 
              />
            )}
            <Text>Select Players - {teamInfo?.name}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
                         <ModalBody>
                   <VStack spacing={4}>
                     {/* Search Input */}
                     <Box w="100%">
                       <InputGroup>
                         <Input
                           placeholder="Search players by name or position..."
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           bg={bgColor}
                           borderColor={borderColor}
                           _focus={{ borderColor: accentColor }}
                         />
                         <InputLeftElement>
                           <Icon as={FaSearch} color={secondaryText} />
                         </InputLeftElement>
                         {searchTerm && (
                           <InputRightElement>
                             <Icon
                               as={FaTimes}
                               color={secondaryText}
                               cursor="pointer"
                               _hover={{ color: textColor }}
                               onClick={() => setSearchTerm('')}
                               boxSize={4}
                             />
                           </InputRightElement>
                         )}
                       </InputGroup>
                     </Box>

                     {/* Selected Players Chips */}
                     {modalSelectedPlayers.length > 0 && (
                       <Box w="100%">
                         <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2}>
                           Selected Players ({modalSelectedPlayers.length})
                         </Text>
                         <HStack spacing={2} flexWrap="wrap">
                           {modalSelectedPlayers.map(player => (
                             <Tag
                               key={player.id}
                               size="md"
                               colorScheme="blue"
                               borderRadius="full"
                               variant="solid"
                             >
                               {httpsPlayerImageUrl(player.rwPlayer.image) ? (
                                 <Box 
                                   w="16px" 
                                   h="16px" 
                                   borderRadius="full" 
                                   overflow="hidden" 
                                   bg="transparent"
                                   ml={-1}
                                   mr={2}
                                 >
                                   <img 
                                     src={httpsPlayerImageUrl(player.rwPlayer.image)} 
                                     alt={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                     style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                     onError={(e) => {
                                       e.currentTarget.style.display = 'none';
                                     }}
                                   />
                                 </Box>
                               ) : (
                                 <Avatar
                                   size="xs"
                                   name={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                   bg={teamInfo?.color_primary || 'gray.300'}
                                   color={teamInfo?.color_text || 'white'}
                                   border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
                                   ml={-1}
                                   mr={2}
                                 />
                               )}
                               <TagLabel>
                                 {player.rwPlayer.first_name} {player.rwPlayer.last_name}
                               </TagLabel>
                               <TagCloseButton
                                 onClick={() => handleRemovePlayer(player.id)}
                                 _hover={{ bg: 'red.500', color: 'white' }}
                               />
                             </Tag>
                           ))}
                         </HStack>
                       </Box>
                     )}
         
                     {/* Players List */}
                     <Box 
                       w="100%" 
                       h="400px" 
                       overflowY="auto" 
                       border="1px solid" 
                       borderColor={borderColor} 
                       borderRadius="md"
                       tabIndex={0}
                       onWheel={(e) => {
                         e.currentTarget.scrollTop += e.deltaY;
                         e.preventDefault();
                       }}
                       onTouchStart={(e) => {
                         const touch = e.touches[0];
                         e.currentTarget.setAttribute('data-touch-start-y', touch.clientY.toString());
                       }}
                       onTouchMove={(e) => {
                         const touch = e.touches[0];
                         const startY = parseInt(e.currentTarget.getAttribute('data-touch-start-y') || '0');
                         const deltaY = startY - touch.clientY;
                         e.currentTarget.scrollTop += deltaY;
                         e.currentTarget.setAttribute('data-touch-start-y', touch.clientY.toString());
                         e.preventDefault();
                       }}
                       css={{
                         '&::-webkit-scrollbar': {
                           width: '8px',
                         },
                         '&::-webkit-scrollbar-track': {
                           background: 'transparent',
                         },
                         '&::-webkit-scrollbar-thumb': {
                           background: '#718096',
                           borderRadius: '4px',
                         },
                         '&::-webkit-scrollbar-thumb:hover': {
                           background: '#4A5568',
                         },
                         scrollbarWidth: 'thin',
                         scrollbarColor: '#718096 transparent',
                         WebkitOverflowScrolling: 'touch',
                       }}
                     >
                       {isLoading ? (
                         <VStack justify="center" h="100%">
                           <Spinner size="lg" color={accentColor} />
                           <Text color={secondaryText}>Loading players...</Text>
                         </VStack>
                       ) : (
                         <VStack spacing={4} p={4} align="stretch">
                           {getPlayersBySection().map(([sectionLabel, players]) => {
                             // Display "MLB" instead of "Active Roster"
                             const displayLabel = sectionLabel === 'Active Roster' ? 'MLB' : sectionLabel;
                             return (
                             <Box key={sectionLabel}>
                               <Text fontWeight="bold" color={textColor} mb={2} fontSize="lg" position="sticky" top={0} bg={bgColor} py={2} zIndex={1}>
                                 {displayLabel}
                               </Text>
                               <VStack spacing={2} align="stretch">
                                 {players.map(player => {
                                   const positions = formatPlayerPositionsLine(player);
                                   const salary = player.playerContract?.current_salary || player.contract?.current_salary || 0;
                                   const formattedSalary = salary ? `$${(salary / 1000000).toFixed(2)}M` : 'Min';
                                   const isSelected = modalSelectedPlayers.some(p => p.id === player.id);
                                  // Determine if the player is not eligible due to deal status
                                  const dealStatusRaw: string = (
                                    ((player as any)?.deal_status as string | undefined) ||
                                    ((player as any)?.dealData && (player as any).dealData.status) ||
                                    ''
                                  ) as string;
                                  const dealStatusLower = (dealStatusRaw || '').toString().toLowerCase();
                                  const isDisabled = dealStatusLower.includes('waiv') || dealStatusLower.includes('claim') || dealStatusLower.includes('free_agent') || dealStatusLower.includes('review') || dealStatusLower.includes('settlement') || (player as any).deal_status === 'franchise_tag';
                                  
                                  // Determine the display text based on status
                                  let statusDisplay = '';
                                  if (dealStatusRaw) {
                                    if (dealStatusLower.includes('free_agent')) {
                                      statusDisplay = 'Released';
                                    } else if (dealStatusLower.includes('review')) {
                                      statusDisplay = 'Traded';
                                    } else if (dealStatusLower.includes('settlement')) {
                                      statusDisplay = 'Deal Closing Today';
                                    } else if (dealStatusLower.includes('claim')) {
                                      statusDisplay = 'Claimed on waivers';
                                    } else {
                                      statusDisplay = dealStatusRaw.replace(/_/g, ' ');
                                    }
                                  }
                                   
                                   return (
                                     <Box
                                       key={player.id}
                                       p={3}
                                       border="1px solid"
                                      borderColor={isSelected ? accentColor : borderColor}
                                       borderRadius="md"
                                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                                      opacity={isDisabled ? 0.6 : 1}
                                      bg={isSelected ? `${accentColor}20` : "transparent"}
                                      _hover={isDisabled ? {} : { bg: hoverBg }}
                                      onClick={() => { if (!isDisabled) { handlePlayerSelect(player); } }}
                                     >
                                       <HStack spacing={3}>
                                         {httpsPlayerImageUrl(player.rwPlayer.image) ? (
                                           <Box 
                                             w="32px" 
                                             h="32px" 
                                             borderRadius="full" 
                                             overflow="hidden" 
                                             bg="transparent"
                                           >
                                             <img 
                                               src={httpsPlayerImageUrl(player.rwPlayer.image)} 
                                               alt={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                               style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                               onError={(e) => {
                                                 e.currentTarget.style.display = 'none';
                                               }}
                                             />
                                           </Box>
                                         ) : (
                                           <Avatar
                                             size="sm"
                                             name={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                             bg={teamInfo?.color_primary || 'gray.300'}
                                             color={teamInfo?.color_text || 'white'}
                                             border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
                                           />
                                         )}
                                         <Box flex="1">
                                           <Text fontWeight="semibold" color={textColor}>
                                             {player.rwPlayer.first_name} {player.rwPlayer.last_name}
                                           </Text>
                                           <Text fontSize="sm" color={secondaryText}>
                                             {positions} • {formattedSalary}
                                           </Text>
                                         </Box>
                                         {isDisabled && statusDisplay && (
                                           <Badge colorScheme="red" size="sm" title="Player cannot be included in trades right now">
                                             {statusDisplay}
                                           </Badge>
                                         )}
                                         {player.is_40_man && (
                                           <Badge colorScheme="blue" size="sm">40-Man</Badge>
                                         )}
                                         {isSelected && (
                                           <Badge colorScheme="green" size="sm">Selected</Badge>
                                         )}
                                       </HStack>
                                     </Box>
                                   );
                                 })}
                               </VStack>
                             </Box>
                             );
                           })}
                         </VStack>
                       )}
                     </Box>
                                      </VStack>
                 </ModalBody>
                 
                 <ModalFooter borderTop="1px solid" borderColor={borderColor} pt={4}>
                   <HStack spacing={3}>
                     <Button variant="outline" onClick={onClose}>
                       Cancel
                     </Button>
                     <Button
                       colorScheme="blue"
                       onClick={handleDone}
                      //  isDisabled={modalSelectedPlayers.length === 0}
                     >
                       Done ({modalSelectedPlayers.length} selected)
                     </Button>
                   </HStack>
                 </ModalFooter>
               </ModalContent>
             </Modal>
  );
};

export default PlayerSelectionModal; 